import { ScrollDispatcher } from '@angular/cdk/overlay';
import { isPlatformBrowser } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import type { Currency } from '@freelancer/datastore/collections';
import {
  FreelancerCurrencyPipe,
  type AmountRange,
  type CurrencyFormatOptions,
} from '@freelancer/pipes';
import { ScreenSizeService } from '@freelancer/screen-size';
import { TimeUtils } from '@freelancer/time-utils';
import { TrackingModule } from '@freelancer/tracking';
import { IconModule } from '@freelancer/ui/icon';
import { InputComponent } from '@freelancer/ui/input';
import { LoadingTemplateComponent } from '@freelancer/ui/loading-template';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import {
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  of,
  type Observable,
  startWith,
  Subscription,
} from 'rxjs';
import { FlagComponent } from '../flag/flag.component';
import { trackById } from '../helpers/helpers';
import { IconSize } from '../icon';
import { InputEditInlineType, InputSize, InputType } from '../input';
import { SelectItem } from '../select';
import type {
  BackgroundColor,
  BudgetOption,
  ControlType,
  CurrencyInputType,
} from './currency-input.types';

@UntilDestroy({ className: 'CurrencyInputComponent' })
@Component({
  standalone: true,
  selector: 'fl-currency-input',
  imports: [
    FlagComponent,
    IconModule,
    InputComponent,
    LoadingTemplateComponent,
    TrackingModule,
    ValidationErrorComponent,
  ],
  template: `
    <div
      class="CurrencyInputContainer mb-xxxsmall"
      flTrackingSection="CurrencyInput"
      [attr.data-background-color]="backgroundColor"
      [attr.data-type]="type"
      [class.HasBudgetType]="budgetTypeControl && showBudgetTypeSelector"
      [class.IsFocused]="showingDropdown !== 'none'"
      [class.IsInvalid]="hasError"
      [class.IsLoading]="loading"
      [class.IsNeutral]="!hasError && !isValid"
      [class.IsValid]="isValid"
      [@shakeAnimation]="{
        value: shakeState,
        params: {
          duration: 200,
          translateX: '3px',
        },
      }"
      (@shakeAnimation.done)="shakeDone()"
    >
      <!-- Budget Type -->
      @if (budgetTypeControl && showBudgetTypeSelector) {
        <div class="BudgetTypeSelectContainer">
          <div
            class="BudgetType Select"
            flTrackingLabel="BudgetTypeSelect"
            [attr.tabindex]="loading ? -1 : 0"
            (click)="handleDropdownClick('budgetType')"
            (keydown.enter)="
              showingDropdown === 'budgetType'
                ? selectHighlightedItem()
                : handleDropdownClick('budgetType')
            "
          >
            @if (loading) {
              <fl-loading-template class="Skeleton" />
            } @else {
              <div class="BudgetType-content">
                @if (budgetTypeControl?.value) {
                  <p class="text-body-16 NoHighlight Select-text">
                    {{ currentBudgetTypeOption?.displayText }}
                  </p>
                }
              </div>
            }
            <fl-icon
              class="Chevron"
              [fillIconFont]="true"
              [name]="
                showingDropdown === 'budgetType'
                  ? 'ui-chevron-up'
                  : 'ui-chevron-down'
              "
              [size]="IconSize.MID"
              [useIconFont]="true"
            ></fl-icon>
          </div>

          <!-- Budget Type Dropdown -->
          @if (showingDropdown === 'budgetType') {
            <div
              class="BudgetType Dropdown"
              [class.IsAbove]="dropdownDirection === 'up'"
              [class.IsKeyboardNavigating]="isKeyboardNavigating"
              [class.ShowAllOptions]="showAllDropdownOptions"
              (mousemove)="handleMouseMove()"
            >
              @if (budgetTypeOptions; as options) {
                @for (option of options; track trackById($index, option)) {
                  <div
                    class="Dropdown-item"
                    [attr.tabindex]="0"
                    [class.IsHighlighted]="highlightedOptionIndex === $index"
                    (click)="selectBudgetType(option)"
                    (mouseenter)="updateHighlightedOptionIndex($index)"
                  >
                    @if (budgetTypeControl?.value) {
                      <p class="text-body-16">
                        {{ option.displayText }}
                      </p>
                    }
                  </div>
                }
              }
            </div>
          }
        </div>
      }

      @if (label) {
        <label
          class="Label"
          [attr.data-background-color]="backgroundColor"
          [attr.data-type]="type"
          [class.IsBudgetEmpty]="
            !loading &&
            isNumberControl(budgetControl) &&
            isBudgetEmpty(budgetControl)
          "
          [class.IsInvalid]="hasError"
          [class.IsValid]="isValid"
        >
          {{ label }}
        </label>
      }
      <!-- Budget Input -->
      @if (
        type === 'input' && budgetControl && isNumberControl(budgetControl)
      ) {
        <div class="Budget InputContainer">
          @if (loading) {
            <fl-loading-template class="Skeleton" />
          } @else {
            <fl-input
              class="Budget Input"
              flTrackingLabel="BudgetInput"
              [beforeLabel]="currencyControl?.value.sign"
              [control]="budgetControl"
              [displayValidation]="false"
              [editInline]="InputEditInlineType.NON_BORDERED"
              [showIncrement]="false"
              [size]="InputSize.LARGE"
              [type]="InputType.NUMBER"
              (onBlur)="handleBlur()"
            />
          }
        </div>
      }

      <!-- Budget Selector -->
      @if (
        type === 'dropdown' &&
        budgetControl &&
        isBudgetOptionControl(budgetControl)
      ) {
        <div class="BudgetSelectContainer">
          @if (budgetOptions || loading) {
            <div
              class="Budget Select"
              flTrackingLabel="BudgetSelect"
              [attr.tabindex]="loading ? -1 : 0"
              (click)="handleDropdownClick('budget')"
              (keydown.enter)="
                showingDropdown === 'budget'
                  ? selectHighlightedItem()
                  : handleDropdownClick('budget')
              "
            >
              @if (loading) {
                <fl-loading-template class="Skeleton" />
              } @else if (currencyControl?.value && budgetControl?.value) {
                <p class="text-body-16 NoHighlight Select-text">
                  {{ budgetControl.value.displayText }}
                </p>
              }
              <fl-icon
                class="Chevron"
                [fillIconFont]="true"
                [name]="
                  showingDropdown === 'budget'
                    ? 'ui-chevron-up'
                    : 'ui-chevron-down'
                "
                [size]="IconSize.MID"
                [useIconFont]="true"
              ></fl-icon>
            </div>
          }

          <!-- Budget Dropdown -->
          @if (type === 'dropdown' && showingDropdown === 'budget') {
            <div
              class="Budget Dropdown"
              [class.IsAbove]="dropdownDirection === 'up'"
              [class.IsKeyboardNavigating]="isKeyboardNavigating"
              [class.ShowAllOptions]="showAllDropdownOptions"
              (mousemove)="handleMouseMove()"
            >
              @if (formattedBudgetOptions; as budgets) {
                @for (budget of budgets; track trackById($index, budget)) {
                  <div
                    class="Dropdown-item"
                    [attr.tabindex]="0"
                    [class.IsHighlighted]="highlightedOptionIndex === $index"
                    (click)="selectBudget(budget)"
                    (mouseenter)="updateHighlightedOptionIndex($index)"
                  >
                    @if (currencyControl?.value && budgetControl?.value) {
                      <p class="text-body-16">
                        {{ budget.displayText }}
                      </p>
                    }
                  </div>
                }
              }
            </div>
          }
        </div>
      }

      <!-- Currency Selector -->
      <div
        class="CurrencySelectContainer"
        [class.Alone]="!budgetControl && !budgetOptions"
      >
        <div
          class="Currency Select"
          flTrackingLabel="CurrencySelect"
          [attr.tabindex]="loading ? -1 : 0"
          [class.Alone]="!budgetControl && !budgetOptions"
          (click)="handleDropdownClick('currency')"
          (keydown.enter)="
            showingDropdown === 'currency'
              ? selectHighlightedItem()
              : handleDropdownClick('currency')
          "
        >
          @if (loading) {
            <fl-loading-template class="Skeleton" />
          } @else {
            <div class="Currency-content">
              @if (currencyControl.value?.country) {
                <fl-flag
                  class="mobile:hide mr-xxxsmall"
                  [countryCode]="currencyControl.value?.country"
                ></fl-flag>
              }
              @if (currencyControl.value) {
                <p class="text-body-16 NoHighlight Select-text">
                  {{ currencyControl.value.code }}
                </p>
              }
            </div>
          }
          <fl-icon
            class="Chevron"
            [fillIconFont]="true"
            [name]="
              showingDropdown === 'currency'
                ? 'ui-chevron-up'
                : 'ui-chevron-down'
            "
            [size]="IconSize.MID"
            [useIconFont]="true"
          ></fl-icon>
        </div>

        <!-- Currency Dropdown -->
        @if (showingDropdown === 'currency') {
          <div
            class="Currency Dropdown"
            [class.IsAbove]="dropdownDirection === 'up'"
            [class.IsKeyboardNavigating]="isKeyboardNavigating"
            [class.ShowAllOptions]="showAllDropdownOptions"
            (mousemove)="handleMouseMove()"
          >
            @if (currencyOptions; as currencies) {
              @for (currency of currencies; track trackById($index, currency)) {
                <div
                  class="Dropdown-item"
                  [attr.tabindex]="0"
                  [class.IsHighlighted]="highlightedOptionIndex === $index"
                  (click)="selectCurrency(currency)"
                  (mouseenter)="updateHighlightedOptionIndex($index)"
                >
                  @if (currency.country) {
                    <fl-flag
                      class="mobile:hide mr-xxxsmall"
                      [countryCode]="currency.country"
                    ></fl-flag>
                  }
                  <p class="text-body-16">
                    {{ currency.code }}
                  </p>
                </div>
              }
            }
          </div>
        }
      </div>
    </div>
    @if (isNumberControl(budgetControl)) {
      <fl-validation-error [control]="budgetControl"></fl-validation-error>
    }
  `,
  styleUrls: ['./currency-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class CurrencyInputComponent implements OnInit, OnChanges, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private currencyPipe = inject(FreelancerCurrencyPipe);
  private elementRef = inject(ElementRef);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private screenSizeService = inject(ScreenSizeService);
  private scrollDispatcher = inject(ScrollDispatcher);
  private timeUtils = inject(TimeUtils);

  IconSize = IconSize;
  InputEditInlineType = InputEditInlineType;
  InputSize = InputSize;
  InputType = InputType;
  ProjectTypeApi = ProjectTypeApi;
  trackById = trackById;

  @Input() backgroundColor: BackgroundColor = 'default';
  @Input() budgetControl?: FormControl<BudgetOption | number>;
  @Input() budgetOptions?: readonly BudgetOption[];
  @Input() budgetTypeControl?: FormControl<ProjectTypeApi>;
  @Input() currencyControl: FormControl<Currency>;
  @Input() currencyFormat: CurrencyFormatOptions = {
    disableCode: true,
  };
  @Input() currencyOptions: readonly Currency[] = [];
  @Input() enableCurrencyConversion = true;
  @Input() label: string;
  @Input() loading = false;
  @Input() showAllDropdownOptions = false;
  @Input() showBudgetTypeSelector = true;
  @Input() type: CurrencyInputType = 'dropdown';

  dropdownDirection: 'up' | 'down' = 'down';
  formattedBudgetOptions: BudgetOption[] = [];
  highlightedOptionIndex = 0;
  isKeyboardNavigating = false;
  isValid = false;
  shakeState: 'void' | 'active' = 'void';
  showingDropdown: 'none' | 'budgetType' | 'budget' | 'currency' = 'none';
  validationStateCheck = false;

  budgetTypeOptions: readonly SelectItem<ProjectTypeApi>[];
  currentBudgetTypeOption: SelectItem<ProjectTypeApi> | undefined;

  private isMobile$: Observable<boolean>;

  private dropdownScrollSubscription?: Subscription;
  private subscriptions = new Subscription();

  // Default cap on dropdown height
  private readonly DROPDOWN_DEFAULT_MAX_HEIGHT = 200;
  // Floor for the dynamic max-height, shows approx. 3 options
  private readonly DROPDOWN_MIN_HEIGHT = 120;
  // Buffer between the dropdown edge and the clipping ancestor / viewport edge
  private readonly DROPDOWN_SAFETY_PADDING = 8;
  // Keep scroll repositioning responsive without running layout reads on every event.
  private readonly DROPDOWN_SCROLL_THROTTLE_MS = 50;

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    // Check if the click was outside the component
    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      this.showingDropdown !== 'none'
    ) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.loading || this.showingDropdown === 'none') {
      return;
    }

    switch (event.key?.toLowerCase()) {
      case 'arrowup':
      case 'up':
        // Prevent page scroll and triggering event handlers in the parent component
        // while navigating the dropdown using keyboard
        event.preventDefault();
        event.stopPropagation();
        this.isKeyboardNavigating = true;
        this.moveUp();
        break;
      case 'arrowdown':
      case 'down':
        event.preventDefault();
        event.stopPropagation();
        this.isKeyboardNavigating = true;
        this.moveDown();
        break;
      case 'escape':
      case 'tab':
        this.closeDropdown();
        this.highlightedOptionIndex = 0;
        break;
      default:
        break;
    }
  }

  // Enter interaction (open/select) is handled on keydown. Stop the
  // corresponding keyup from bubbling to parent keyup.enter listeners.
  @HostListener('keyup', ['$event'])
  handleKeyupEvent(event: KeyboardEvent): void {
    if (event.key?.toLowerCase() === 'enter') {
      event.stopPropagation();
    }
  }

  async ngOnInit(): Promise<void> {
    this.isMobile$ = isPlatformBrowser(this.platformId)
      ? this.screenSizeService.isMobile()
      : of(false);

    if (isPlatformBrowser(this.platformId)) {
      if (this.budgetTypeControl && this.showBudgetTypeSelector) {
        this.subscriptions.add(
          this.screenSizeService
            .isMobileXSmall()
            .pipe(distinctUntilChanged())
            .subscribe(isMobileXSmall => {
              if (isMobileXSmall) {
                this.budgetTypeOptions = [
                  {
                    value: ProjectTypeApi.FIXED,
                    displayText: $localize`:Fixed price option:Pay fixed amount`,
                  },
                  {
                    value: ProjectTypeApi.HOURLY,
                    displayText: $localize`:Hourly price option:Pay hourly rate`,
                  },
                ];
              } else {
                this.budgetTypeOptions = [
                  {
                    value: ProjectTypeApi.FIXED,
                    displayText: $localize`:Fixed price option:Pay fixed`,
                  },
                  {
                    value: ProjectTypeApi.HOURLY,
                    displayText: $localize`:Hourly price option:Pay hourly`,
                  },
                ];
              }
              this.currentBudgetTypeOption = this.budgetTypeOptions.find(
                option => option.value === this.budgetTypeControl?.value,
              );
              this.cd.markForCheck();
            }),
        );
      }

      if (this.isBudgetOptionControl(this.budgetControl)) {
        this.subscriptions.add(
          this.isMobile$
            .pipe(distinctUntilChanged())
            .subscribe(async isMobile => {
              await this.formatBudgetOptions(isMobile);
              this.cd.markForCheck();
            }),
        );
      }
    }

    if (this.isBudgetOptionControl(this.budgetControl) && this.budgetControl) {
      await this.formatBudgetOptions();
      this.getBudget();

      this.subscriptions.add(
        combineLatest([
          this.budgetControl.valueChanges.pipe(
            startWith(this.budgetControl.value),
            distinctUntilChanged(),
          ),
          this.currencyControl.valueChanges.pipe(
            startWith(this.currencyControl.value),
            distinctUntilChanged(),
          ),
        ]).subscribe(async () => {
          await this.formatBudgetOptions();
          // Update view when controls are updated outside the component
          this.cd.markForCheck();
        }),
      );
    }

    if (this.isNumberControl(this.budgetControl)) {
      this.subscriptions.add(
        this.budgetControl.statusChanges.subscribe(() => {
          if (
            this.budgetControl?.invalid &&
            (this.budgetControl?.touched || this.budgetControl?.dirty) &&
            Number.isNaN(this.budgetControl?.value) &&
            isDefined(this.budgetControl?.value)
          ) {
            this.validationStateCheck = true;
          }

          if (this.validationStateCheck && this.budgetControl?.valid) {
            this.isValid = true;
          }

          if (this.validationStateCheck && this.budgetControl?.invalid) {
            this.isValid = false;
          }
        }),
      );
    }

    // We don't want to add the showBudgetTypeSelector condition here
    // since this is used to update the displayed budget options text
    if (this.budgetTypeControl && this.budgetTypeOptions) {
      this.subscriptions.add(
        this.budgetTypeControl.valueChanges
          .pipe(startWith(this.budgetTypeControl.value), distinctUntilChanged())
          .subscribe(async budgetType => {
            this.currentBudgetTypeOption = this.budgetTypeOptions.find(
              option => option.value === budgetType,
            );
            await this.formatBudgetOptions();
          }),
      );
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.loading?.currentValue && !changes.loading.isFirstChange()) {
      if (this.showingDropdown !== 'none') {
        this.closeDropdown();
      }
      // Remove focus from the selectors. Otherwise, pressing
      // a key shows a :focus-visible ring around them.
      this.blurSelectors();
    }

    if (changes.currencyOptions?.currentValue) {
      this.getCurrency();
    }

    if (
      !changes.budgetOptions?.isFirstChange() &&
      changes.budgetOptions?.currentValue
    ) {
      await this.formatBudgetOptions();
      this.getBudget();
    }
  }

  moveUp(): void {
    this.highlightedOptionIndex--;
    this.handleNavigationOverflow();
    this.scrollItemIntoView();
  }

  moveDown(): void {
    this.highlightedOptionIndex++;
    this.handleNavigationOverflow();
    this.scrollItemIntoView();
  }

  handleNavigationOverflow(): void {
    if (this.showingDropdown === 'none') {
      return;
    }

    const dropdownLength =
      this.showingDropdown === 'budgetType'
        ? this.budgetTypeOptions.length
        : this.showingDropdown === 'budget'
          ? this.formattedBudgetOptions.length
          : this.currencyOptions.length;

    if (this.highlightedOptionIndex < 0) {
      this.highlightedOptionIndex = dropdownLength - 1;
    } else if (this.highlightedOptionIndex > dropdownLength - 1) {
      this.highlightedOptionIndex = 0;
    }
  }

  updateHighlightedOptionIndex(index: number): void {
    if (!this.isKeyboardNavigating) {
      this.highlightedOptionIndex = index;
    }
  }

  selectHighlightedItem(): void {
    switch (this.showingDropdown) {
      case 'budgetType':
        if (this.budgetTypeOptions && this.highlightedOptionIndex >= 0) {
          this.selectBudgetType(
            this.budgetTypeOptions[this.highlightedOptionIndex],
          );
        }
        break;
      case 'budget':
        if (this.formattedBudgetOptions && this.highlightedOptionIndex >= 0) {
          this.selectBudget(
            this.formattedBudgetOptions[this.highlightedOptionIndex],
          );
        }
        break;
      case 'currency':
        if (this.currencyOptions && this.highlightedOptionIndex >= 0) {
          this.selectCurrency(
            this.currencyOptions[this.highlightedOptionIndex],
          );
        }
        break;
      default:
        break;
    }
  }

  getCurrency(): void {
    if (this.currencyControl.value) {
      const matchedCurrency = this.currencyOptions.find(
        currency => currency.code === this.currencyControl.value?.code,
      );
      if (matchedCurrency) {
        this.selectCurrency(matchedCurrency);
      }
    } else if (this.currencyOptions.length > 0) {
      this.selectCurrency(this.currencyOptions[0]);
    }
  }

  getBudget(): void {
    if (this.isBudgetOptionControl(this.budgetControl) && this.budgetOptions) {
      const currentBudgetValue = this.budgetControl.value;

      if (currentBudgetValue) {
        const matchedBudget = this.formattedBudgetOptions.find(
          budget => budget.id === currentBudgetValue.id,
        );
        if (matchedBudget) {
          this.selectBudget(matchedBudget);
        }
      } else if (this.formattedBudgetOptions.length > 0) {
        this.selectBudget(this.formattedBudgetOptions[0]);
      }
    }
  }

  getBudgetTypeText(isMobile: boolean): string {
    switch (this.budgetTypeControl?.value) {
      case ProjectTypeApi.FIXED:
        return '';
      case ProjectTypeApi.HOURLY:
        return isMobile
          ? $localize`:Hourly budget text:/hr`
          : $localize`:Hourly budget text: per hour`;
      default:
        return isMobile
          ? $localize`:Hourly budget text:/hr`
          : $localize`:Hourly budget text: per hour`;
    }
  }

  getDisplayedBudgetRange(budget: BudgetOption, isMobile: boolean): string {
    const range = this.enableCurrencyConversion
      ? this.getBudgetRange(budget)
      : budget.range;
    if (!range) {
      return '';
    }

    if (
      budget.id === 'custom' &&
      budget.range.minimum === 0 &&
      budget.range.maximum === 0
    ) {
      return $localize`:Dropdown option to set custom budget:Customize budget`;
    }

    const currencyCode = this.currencyControl.value?.code;

    const formattedRange = this.currencyPipe
      .transform(range, currencyCode, this.currencyFormat)
      ?.trim();

    const budgetTypeText = this.getBudgetTypeText(isMobile);

    return isMobile
      ? `${formattedRange}${budgetTypeText}`
      : `${budget.name} (${formattedRange}${budgetTypeText})`;
  }

  selectCurrency(currency: Currency): void {
    this.currencyControl.setValue(currency);
    this.closeDropdown();
  }

  selectBudget(budget: BudgetOption): void {
    if (this.budgetControl) {
      this.budgetControl.setValue(budget);
    }
    this.closeDropdown();
  }

  selectBudgetType(option: SelectItem<ProjectTypeApi>): void {
    if (this.budgetTypeControl) {
      this.budgetTypeControl.setValue(option.value);
    }
    this.closeDropdown();
  }

  getBudgetRange(budget: BudgetOption | undefined): AmountRange | undefined {
    if (!this.currencyControl.value || !budget) {
      return undefined;
    }
    return {
      minimum: this.getConvertedRoundedAmount(
        budget.range.minimum,
        this.currencyControl.value.exchangeRate,
      ),
      maximum: this.getConvertedRoundedAmount(
        budget.range.maximum,
        this.currencyControl.value.exchangeRate,
      ),
    };
  }

  getConvertedRoundedAmount(
    amount?: number,
    targetCurrencyExchangeRate?: number,
  ): number {
    return (amount ?? 1) / (targetCurrencyExchangeRate ?? 1);
  }

  async formatBudgetOptions(mobile?: boolean): Promise<void> {
    if (
      this.budgetControl &&
      this.isBudgetOptionControl(this.budgetControl) &&
      this.budgetOptions &&
      this.currencyControl.value
    ) {
      const isMobile =
        mobile ??
        (await firstValueFrom(this.isMobile$.pipe(untilDestroyed(this))));

      this.formattedBudgetOptions = this.budgetOptions.map(budget => ({
        ...budget,
        displayText: this.getDisplayedBudgetRange(budget, isMobile),
      }));

      const currentBudget = this.budgetControl.value;
      if (currentBudget) {
        this.budgetControl.setValue(
          {
            displayText: this.getDisplayedBudgetRange(currentBudget, isMobile),
            id: currentBudget.id,
            name: currentBudget.name,
            range: currentBudget.range,
          },
          { emitEvent: false },
        );
      }
    }
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  handleBlur(): void {
    if (
      this.isNumberControl(this.budgetControl) &&
      this.budgetControl?.invalid &&
      (this.budgetControl?.touched || this.budgetControl?.dirty)
    ) {
      this.shakeState = 'active';
    }
  }

  handleMouseMove(): void {
    this.isKeyboardNavigating = false;
  }

  private closeDropdown(): void {
    this.showingDropdown = 'none';
    this.dropdownDirection = 'down';
    this.stopDropdownScrollListener();
  }

  private blurSelectors(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const selectors: NodeListOf<HTMLElement> =
      this.elementRef.nativeElement.querySelectorAll('.Select');
    selectors.forEach(selector => selector.blur());
  }

  handleDropdownClick(dropdown: 'budgetType' | 'budget' | 'currency'): void {
    if (this.loading) {
      return;
    }

    this.isKeyboardNavigating = true;
    this.highlightedOptionIndex = 0;
    if (this.showingDropdown === dropdown) {
      this.closeDropdown();
      return;
    }

    this.showingDropdown = dropdown;
    this.startDropdownScrollListener();

    this.timeUtils.setTimeout(() => {
      this.updateDropdownPosition({ updateHeight: true });
    });
  }

  private startDropdownScrollListener(): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      this.dropdownScrollSubscription
    ) {
      return;
    }

    this.dropdownScrollSubscription = this.scrollDispatcher
      .scrolled(this.DROPDOWN_SCROLL_THROTTLE_MS)
      .subscribe(() => {
        // ScrollDispatcher emits outside Angular. Re-enter before updating
        // dropdownDirection so OnPush template bindings refresh after scrolling.
        this.ngZone.run(() => {
          this.updateDropdownPosition({ updateHeight: false });
        });
      });
  }

  private stopDropdownScrollListener(): void {
    this.dropdownScrollSubscription?.unsubscribe();
    this.dropdownScrollSubscription = undefined;
  }

  private updateDropdownPosition({
    updateHeight,
  }: {
    readonly updateHeight: boolean;
  }): void {
    if (this.showingDropdown === 'none') {
      return;
    }

    this.computeDropdownDirectionAndHeight(this.showingDropdown, updateHeight);
    this.cd.markForCheck();
  }

  private computeDropdownDirectionAndHeight(
    dropdown: 'budgetType' | 'budget' | 'currency',
    updateHeight: boolean,
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const containerClass =
      dropdown === 'budgetType'
        ? '.BudgetTypeSelectContainer'
        : dropdown === 'budget'
          ? '.BudgetSelectContainer'
          : '.CurrencySelectContainer';

    const container: HTMLElement | undefined =
      this.elementRef.nativeElement.querySelector(containerClass);
    if (!container) {
      return;
    }

    const dropdownEl = container.querySelector('.Dropdown') as HTMLElement;
    if (!dropdownEl) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const clippingAncestor = this.findClippingAncestor(container);
    const ancestorRect = clippingAncestor?.getBoundingClientRect();
    const topBound = ancestorRect ? ancestorRect.top : 0;
    const bottomBound = ancestorRect ? ancestorRect.bottom : window.innerHeight;

    const spaceBelow = bottomBound - rect.bottom;
    const spaceAbove = rect.top - topBound;

    // Dropdown opens downward if it fits below. Only flip upward when the dropdown does NOT fit
    // below AND upward has more space than downward.
    const dropdownHeight = updateHeight
      ? this.getDropdownTargetHeight(dropdownEl)
      : dropdownEl.offsetHeight;
    const fitsBelow =
      spaceBelow >= dropdownHeight + this.DROPDOWN_SAFETY_PADDING;
    const openDown = fitsBelow || spaceBelow >= spaceAbove;
    this.dropdownDirection = openDown ? 'down' : 'up';

    if (!updateHeight) {
      return;
    }

    const availableSpace = openDown ? spaceBelow : spaceAbove;
    const usableSpace = availableSpace - this.DROPDOWN_SAFETY_PADDING;

    // When the consumer opts into `showAllDropdownOptions` and the full list
    // fits in the available space, max-height remains unset.
    if (this.showAllDropdownOptions && dropdownEl.scrollHeight <= usableSpace) {
      return;
    }

    // Otherwise constrain the max-height so the dropdown
    // scrolls instead of being visually clipped.
    const cap = this.showAllDropdownOptions
      ? usableSpace
      : Math.min(this.DROPDOWN_DEFAULT_MAX_HEIGHT, usableSpace);
    const maxHeight = Math.max(this.DROPDOWN_MIN_HEIGHT, cap);
    this.renderer.setStyle(dropdownEl, 'max-height', `${maxHeight}px`);
  }

  private getDropdownTargetHeight(dropdownEl: HTMLElement): number {
    return this.showAllDropdownOptions
      ? dropdownEl.scrollHeight
      : Math.min(dropdownEl.scrollHeight, this.DROPDOWN_DEFAULT_MAX_HEIGHT);
  }

  /**
   * Walks up the DOM to find the nearest ancestor with overflow clipping
   * (overflow: hidden, clip, scroll, or auto) that would clip an absolutely
   * positioned dropdown extending beyond its bounds.
   */
  private findClippingAncestor(element: HTMLElement): HTMLElement | undefined {
    let current = element.parentElement;

    while (
      current &&
      current !== document.body &&
      current !== document.documentElement
    ) {
      const { overflowY } = getComputedStyle(current);

      if (
        overflowY === 'hidden' ||
        overflowY === 'clip' ||
        overflowY === 'scroll' ||
        overflowY === 'auto'
      ) {
        return current;
      }

      current = current.parentElement;
    }

    return undefined;
  }

  scrollItemIntoView(): void {
    const containerClass =
      this.showingDropdown === 'budgetType'
        ? '.BudgetTypeSelectContainer'
        : this.showingDropdown === 'budget'
          ? '.BudgetSelectContainer'
          : '.CurrencySelectContainer';

    const container =
      this.elementRef.nativeElement.querySelector(containerClass);
    if (!container) {
      return;
    }

    const elements = container.querySelectorAll('.Dropdown-item');
    if (elements[this.highlightedOptionIndex]) {
      const e = elements[this.highlightedOptionIndex] as Element & {
        scrollIntoViewIfNeeded?(): void;
      };

      if (e.scrollIntoViewIfNeeded) {
        e.scrollIntoViewIfNeeded();
      } else {
        e.scrollIntoView();
      }
    }
  }

  isBudgetEmpty(control: FormControl<number>): boolean {
    return !isDefined(control.value) || Number.isNaN(control.value);
  }

  isBudgetOptionControl(
    control?: FormControl<BudgetOption | number>,
  ): control is FormControl<BudgetOption> {
    return this.controlType === 'budgetOption';
  }

  isNumberControl(
    control?: FormControl<BudgetOption | number>,
  ): control is FormControl<number> {
    return this.controlType === 'number';
  }

  get controlType(): ControlType {
    switch (this.type) {
      case 'input':
        return 'number';
      case 'dropdown':
        return 'budgetOption';
      default:
        return 'number';
    }
  }

  get hasError(): boolean {
    if (
      isDefined(this.budgetControl) &&
      this.isNumberControl(this.budgetControl)
    ) {
      return (
        this.budgetControl.invalid &&
        (this.budgetControl.touched || this.budgetControl.dirty) &&
        isDefined(this.budgetControl.value)
      );
    }
    return false;
  }

  ngOnDestroy(): void {
    this.stopDropdownScrollListener();
    this.subscriptions.unsubscribe();
  }
}
